import React, { useState, useEffect } from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Typography,
	Box,
	Chip,
	CircularProgress,
	Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { format } from "date-fns";

// Styled components using bank colors
const StyledTableHead = styled(TableHead)(({ theme }) => ({
	backgroundColor: "#D42E11",
	"& .MuiTableCell-head": {
		backgroundColor: "#D42E11",
		color: "#FFFFFF",
		fontWeight: "bold",
		fontSize: "0.875rem",
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: "rgba(212, 46, 17, 0.04)",
	},
	"&:hover": {
		backgroundColor: "rgba(212, 46, 17, 0.08)",
	},
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
	width: "100%",
	overflow: "hidden",
	boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
	border: "1px solid rgba(212, 46, 17, 0.1)",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
	backgroundColor: "#D42E11",
	color: "#FFFFFF",
	padding: theme.spacing(2),
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
}));

const UploadedFilesTables = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// Fetch data from PHP API
	useEffect(() => {
		const fetchFiles = async () => {
			try {
				setLoading(true);
				// Replace with your actual API endpoint
				const response = await axios.get(
					"https://bulksms.approot.ng/approvefiles.php"
				);

				if (response.data.success) {
					setFiles(response.data.data);
				} else {
					setError(response.data.error || "Failed to fetch data");
				}
			} catch (err) {
				setError("Error connecting to server: " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchFiles();
	}, []);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Format date for display
	const formatDate = (dateString) => {
		try {
			return format(new Date(dateString), "dd/MM/yyyy HH:mm");
		} catch (error) {
			return dateString;
		}
	};

	// Truncate long text
	const truncateText = (text, maxLength = 30) => {
		if (!text) return "-";
		return text.length > maxLength
			? text.substring(0, maxLength) + "..."
			: text;
	};

	if (loading) {
		return (
			<Box
				display='flex'
				justifyContent='center'
				alignItems='center'
				height='400px'
			>
				<CircularProgress style={{ color: "#D42E11" }} />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity='error' style={{ margin: "20px 0" }}>
				{error}
			</Alert>
		);
	}

	return (
		<StyledPaper>
			<HeaderBox>
				<Typography variant='h5' component='h2'>
					Pushed Files Report
				</Typography>
				<Chip
					label={`${files.length} Records`}
					style={{
						backgroundColor: "#FFFFFF",
						color: "#D42E11",
						fontWeight: "bold",
					}}
				/>
			</HeaderBox>

			<TableContainer style={{ maxHeight: 600 }}>
				<Table stickyHeader>
					<StyledTableHead>
						<TableRow>
							<TableCell>Created At</TableCell>
							<TableCell>Batch ID</TableCell>
							<TableCell>Message Category</TableCell>
							<TableCell>Cost Center</TableCell>
							<TableCell>Total Count</TableCell>
							<TableCell>Total Distinct</TableCell>
							<TableCell>Uploaded By</TableCell>
							<TableCell>Approved By</TableCell>
							<TableCell>Raw Message</TableCell>
						</TableRow>
					</StyledTableHead>
					<TableBody>
						{files
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((file, index) => (
								<StyledTableRow key={index}>
									<TableCell>{formatDate(file.created_at)}</TableCell>
									<TableCell>
										<Typography
											variant='body2'
											style={{ fontFamily: "monospace" }}
										>
											{file.batch_id || "-"}
										</Typography>
									</TableCell>
									<TableCell>
										{file.msg_cat ? (
											<Chip
												label={file.msg_cat}
												size='small'
												style={{
													backgroundColor: "rgba(212, 46, 17, 0.1)",
													color: "#D42E11",
												}}
											/>
										) : (
											"-"
										)}
									</TableCell>
									<TableCell>{file.cost_cntr || "-"}</TableCell>
									<TableCell>
										<Typography variant='body2' align='center'>
											{file.total_count || "0"}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant='body2' align='center'>
											{file.total_distinct || "0"}
										</Typography>
									</TableCell>
									<TableCell>{file.uploaded_by || "-"}</TableCell>
									<TableCell>{file.approved_by || "-"}</TableCell>
									<TableCell>
										<Typography variant='body2' title={file.rawMessage}>
											{truncateText(file.rawMessage)}
										</Typography>
									</TableCell>
								</StyledTableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePagination
				component='div'
				count={files.length}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				rowsPerPageOptions={[5, 10, 25, 50]}
				style={{
					borderTop: "1px solid rgba(212, 46, 17, 0.1)",
					backgroundColor: "rgba(212, 46, 17, 0.02)",
				}}
			/>
		</StyledPaper>
	);
};

export default UploadedFilesTables;
